import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function Broadcasts() {
  const [forms, setForms] = useState([]);
  const [history, setHistory] = useState(null);
  const [body, setBody] = useState("");
  const [formId, setFormId] = useState("all");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/business/broadcasts").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    api.get("/business/forms").then(({ data }) => setForms(data)).catch(() => {});
    load();
  }, []);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/business/broadcasts", { body, form_id: formId === "all" ? null : formId });
      toast.success(`Broadcast sent to ${data.recipients} customer(s)`);
      setBody("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="broadcasts-page">
      <h1 className="text-3xl font-black">Broadcasts</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Send one message to all your customers (or just one form's customers). Each customer gets it in their own conversation thread, plus an email.
      </p>

      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-4">
        <div className="space-y-2">
          <Label>Audience</Label>
          <Select value={formId} onValueChange={setFormId}>
            <SelectTrigger className="w-64" data-testid="broadcast-audience-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all" data-testid="audience-all">All customers</SelectItem>
              {forms.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`audience-${f.id}`}>{f.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Message</Label>
          <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your announcement…" data-testid="broadcast-body-input" />
        </div>
        <Button onClick={submit} disabled={sending || !body.trim()} className="rounded-full px-6" data-testid="broadcast-send-button">
          <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending…" : "Send broadcast"}
        </Button>
      </div>

      <h2 className="mt-10 text-base md:text-lg font-bold">History</h2>
      <div className="mt-4 space-y-3">
        {!history ? (
          <p className="text-sm text-neutral-400">Loading…</p>
        ) : history.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center" data-testid="broadcasts-empty">
            <Megaphone className="h-10 w-10 text-neutral-200 mx-auto" />
            <p className="text-sm text-neutral-400 mt-3">No broadcasts sent yet.</p>
          </div>
        ) : (
          history.map((b) => (
            <div key={b.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`broadcast-${b.id}`}>
              <p className="text-sm whitespace-pre-wrap">{b.body}</p>
              <div className="text-xs text-neutral-400 mt-3">
                {b.recipients} recipient(s) · {new Date(b.created_at).toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
